import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { FooterDensity } from "../FooterButton";

// Вътрешният отстъп на лентата от двете страни (`px-5`).
const BAR_PADDING_PX = 20;

/** Разстоянието между бутоните на всяка степен на свиване. */
export const FOOTER_ROW_GAP_CLASS: Record<FooterDensity, string> = {
  normal: "gap-2.5",
  compact: "gap-2",
  tight: "gap-1.5",
  squeeze: "gap-1.5",
};

/**
 * Колко да се свият бутоните в лентата под масите, за да се съберат ВСИЧКИ —
 * ограничение за броя няма. `FooterMeasureRow` рисува невидимо целия ред на
 * степените normal → compact → tight; избира се първата, която се събира в
 * лентата. Не се събира ли и tight — squeeze: бутоните се свиват заедно
 * (flex-shrink) и текстът им се реже с „…".
 */
export const useFooterDensity = (enabled: boolean) => {
  const barRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [density, setDensity] = useState<FooterDensity>("normal");

  const measure = useCallback(() => {
    const bar = barRef.current;
    const measureBox = measureRef.current;
    if (!bar || !measureBox) return;
    const availableWidth = bar.clientWidth - 2 * BAR_PADDING_PX;
    let next: FooterDensity = "squeeze";
    for (const row of measureBox.querySelectorAll<HTMLElement>(
      "[data-density]",
    )) {
      if (row.offsetWidth <= availableWidth) {
        next = row.dataset.density as FooterDensity;
        break;
      }
    }
    setDensity((previous) => (previous === next ? previous : next));
  }, []);

  // След всеки рендер — редът се сменя при влачене, смяна на езика и правата;
  // setState-ът е само при реална разлика, затова няма цикъл.
  useLayoutEffect(() => {
    if (enabled) measure();
  });

  // Смяна на размера на прозореца и късно заредени шрифтове.
  useLayoutEffect(() => {
    if (!enabled || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    if (barRef.current) observer.observe(barRef.current);
    measureRef.current
      ?.querySelectorAll("[data-density]")
      .forEach((row) => observer.observe(row));
    return () => observer.disconnect();
  }, [enabled, measure]);

  return { density: enabled ? density : "normal", barRef, measureRef };
};
